import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * SÉLECTEUR DE DIAPO CANVA.
 *
 * Le geste qu'il remplace : ouvrir Canva, exporter la page en PNG, la
 * convertir, l'uploader, coller l'URL. Ici on choisit une présentation, on
 * regarde les pages, on en désigne une, et elle arrive sur Cloudinary.
 *
 * DEUX ENTRÉES, UN SEUL ÉCRAN. Sans `slot`, on part du catalogue : c'est une
 * nouvelle image. Avec un `slot`, l'image existe déjà et on vient la mettre à
 * jour : on saute directement aux pages du bon design, l'image en place
 * affichée à côté de la grille.
 *
 * POURQUOI MONTRER L'IMAGE ACTUELLE plutôt que de simplement resélectionner
 * l'ancien numéro de page. Remanier un deck Canva déplace les diapos : la
 * page 7 d'hier est la 9 d'aujourd'hui. Repartir du numéro mémorisé
 * republierait silencieusement la mauvaise diapo. La comparaison visuelle est
 * la seule vérification qui tient, et c'est à l'auteur de la faire.
 *
 * L'URL, ELLE, NE BOUGE PAS. Le backend réécrit le même fichier Cloudinary
 * sous le même jeton, quel que soit le numéro de page retenu — les ateliers
 * qui pointent dessus n'ont rien à changer.
 */

const API = process.env.REACT_APP_API_URL;
const PAGE_BATCH = 24;

/**
 * Reconnaît une image issue de ce dispositif et en relit la provenance.
 *
 * Le chemin Cloudinary est de la forme `canva_slides/{design}/{jeton}` : tout
 * ce qu'il faut pour rouvrir le bon design et réécrire le bon fichier tient
 * donc dans l'URL. C'est ce qui permet de ne RIEN stocker d'autre — ni champ
 * supplémentaire dans les documents du jeu, ni table de correspondance côté
 * serveur.
 *
 * Le numéro de page, lui, n'est volontairement pas mémorisé : remanier un deck
 * déplace les diapos, et le rejouer à l'aveugle republierait la mauvaise.
 */
export function canvaSourceFromUrl(url) {
  const match = /\/canva_slides\/([A-Za-z0-9_-]+)\/([0-9a-f]{12})(?:\.[a-z0-9]+)?(?:[?#]|$)/i
    .exec(url || "");
  return match ? { designId: match[1], slot: match[2] } : null;
}

export default function CanvaPicker({ token, source, currentUrl, onPublished, onClose }) {
  const slot = source?.slot || null;
  const [status, setStatus] = useState(null); // null = en cours de vérification
  const [designs, setDesigns] = useState([]);
  const [query, setQuery] = useState("");
  const [design, setDesign] = useState(null); // { id, title }
  const [pages, setPages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [busy, setBusy] = useState(null); // libellé de l'action en cours
  const [error, setError] = useState(null);

  // Évite de relancer l'amorçage à chaque rendu tout en gardant le composant
  // pilotable par ses props.
  const bootstrapped = useRef(false);

  const api = useCallback(
    async (method, path, body) => {
      const res = await fetch(`${API}${path}`, {
        method,
        headers: {
          Authorization: "Bearer " + token,
          ...(body ? { "Content-Type": "application/json" } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const data = await res.json().catch(() => ({ error: "Réponse illisible du serveur." }));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    },
    [token]
  );

  // ── Connexion ──────────────────────────────────────────────────────

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await api("GET", "/canva/status"));
    } catch (e) {
      setStatus({ connected: false });
      setError(e.message);
    }
  }, [api]);

  const connect = async () => {
    setError(null);
    try {
      const { url } = await api("POST", "/canva/authorize_url");
      // Popup et non redirection : l'auteur est souvent au milieu d'une
      // édition non sauvegardée quand il découvre qu'il faut se connecter.
      window.open(url, "canva-oauth", "width=760,height=820");
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    const onMessage = (event) => {
      if (!event.data || event.data.source !== "canva-oauth") return;
      if (event.data.success) {
        setError(null);
        bootstrapped.current = false; // relance l'amorçage avec la connexion
        loadStatus();
      } else {
        setError(event.data.message || "Autorisation refusée.");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [loadStatus]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // ── Chargement des designs et des pages ────────────────────────────

  const loadDesigns = useCallback(
    async (search) => {
      setBusy("Chargement des présentations…");
      setError(null);
      try {
        const path = "/canva/designs" + (search ? `?query=${encodeURIComponent(search)}` : "");
        setDesigns((await api("GET", path)).items || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setBusy(null);
      }
    },
    [api]
  );

  const loadPages = useCallback(
    async (designId, alreadyLoaded) => {
      setBusy("Chargement des pages…");
      setError(null);
      try {
        const data = await api(
          "GET",
          `/canva/designs/${designId}/pages?offset=${alreadyLoaded + 1}&limit=${PAGE_BATCH}`
        );
        const items = data.items || [];
        setPages((prev) => (alreadyLoaded ? [...prev, ...items] : items));
        // Un lot incomplet signale la fin, y compris quand le nombre total de
        // pages n'est pas connu.
        setHasMore(items.length === PAGE_BATCH);
      } catch (e) {
        setError(e.message);
      } finally {
        setBusy(null);
      }
    },
    [api]
  );

  const openDesign = useCallback(
    (chosen, preselect) => {
      setDesign(chosen);
      setPages([]);
      setSelectedPage(preselect ?? null);
      loadPages(chosen.id, 0);
    },
    [loadPages]
  );

  // Amorçage : mise à jour d'une image existante → on va droit à son design,
  // dont l'identifiant se lit dans l'URL. Aucune page présélectionnée, c'est
  // délibéré (cf. l'en-tête du fichier).
  useEffect(() => {
    if (!status?.connected || bootstrapped.current) return;
    bootstrapped.current = true;

    if (source?.designId) openDesign({ id: source.designId, title: null });
    else loadDesigns("");
  }, [status, source, loadDesigns, openDesign]);

  // ── Publication ────────────────────────────────────────────────────

  const publish = async () => {
    setBusy(slot ? "Mise à jour de l'image…" : "Export et envoi sur Cloudinary…");
    setError(null);
    try {
      const data = await api("POST", "/canva/publish", {
        design_id: design.id,
        design_title: design.title,
        page: selectedPage,
        slot: slot || undefined,
      });
      onPublished(data.url);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  // ── Rendu ──────────────────────────────────────────────────────────

  const backToDesigns = () => {
    setDesign(null);
    setPages([]);
    setSelectedPage(null);
    if (!designs.length) loadDesigns(query);
  };

  return (
    <div className="builder-picker-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="builder-picker canva-picker">
        <div className="builder-picker-header">
          <h3>
            {design
              ? `${design.title || "Présentation"} — choisir une page`
              : slot
              ? "Mettre à jour depuis Canva"
              : "Importer une diapo depuis Canva"}
          </h3>
          <button onClick={onClose}>✕</button>
        </div>

        <div className="canva-picker-body">
          {status === null && <p className="canva-picker-hint">Vérification de la connexion…</p>}

          {status && !status.connected && (
            <div className="canva-picker-connect">
              <p>Aucun compte Canva n'est relié à la plateforme.</p>
              <button className="builder-btn-primary" onClick={connect}>
                Connecter le compte Canva
              </button>
              <p className="canva-picker-hint">
                Une fenêtre Canva s'ouvre pour l'autorisation, puis se referme seule.
              </p>
            </div>
          )}

          {status?.connected && !design && (
            <>
              <div className="canva-picker-search">
                <input
                  className="builder-input"
                  value={query}
                  placeholder="Filtrer par titre…"
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadDesigns(query)}
                />
                <button className="builder-btn-secondary" onClick={() => loadDesigns(query)}>
                  Rechercher
                </button>
              </div>
              <div className="canva-design-grid">
                {designs.map((d) => (
                  <button key={d.id} className="canva-design-card" onClick={() => openDesign(d)}>
                    {d.thumbnail?.url ? <img src={d.thumbnail.url} alt="" /> : <span className="canva-thumb-empty" />}
                    <span className="canva-design-title">{d.title || "(sans titre)"}</span>
                    <span className="canva-design-meta">
                      {d.page_count ? `${d.page_count} page(s)` : "—"}
                    </span>
                  </button>
                ))}
              </div>
              {!busy && !designs.length && (
                <p className="canva-picker-hint">Aucune présentation trouvée.</p>
              )}
            </>
          )}

          {status?.connected && design && (
            <>
              {currentUrl && (
                <div className="canva-current">
                  <img src={currentUrl} alt="Diapo actuellement en place" />
                  <div>
                    <strong>Image actuelle</strong>
                    <p className="canva-picker-hint">
                      Comparez-la aux pages ci-dessous : l'ordre des diapos a pu changer
                      depuis la dernière publication.
                    </p>
                  </div>
                </div>
              )}
              <div className="canva-page-grid">
                {pages.map((page) => {
                  const number = page.page_number || page.index;
                  return (
                    <button
                      key={page.id || number}
                      className={`canva-page ${selectedPage === number ? "selected" : ""}`}
                      onClick={() => setSelectedPage(number)}
                    >
                      {page.thumbnail?.url ? (
                        <img loading="lazy" src={page.thumbnail.url} alt="" />
                      ) : (
                        <span className="canva-thumb-empty" />
                      )}
                      <span className="canva-page-num">{number}</span>
                    </button>
                  );
                })}
              </div>
              {!busy && !pages.length && (
                <p className="canva-picker-hint">
                  Ce design n'expose pas de pages (c'est le cas des docs Canva). L'export
                  portera sur le design entier.
                </p>
              )}
              {hasMore && (
                <button
                  className="builder-btn-secondary"
                  style={{ marginTop: 12 }}
                  onClick={() => loadPages(design.id, pages.length)}
                >
                  Charger plus de pages
                </button>
              )}
            </>
          )}

          {busy && <p className="canva-picker-hint">{busy}</p>}
          {error && <p className="canva-picker-error">{error}</p>}
        </div>

        <div className="canva-picker-footer">
          {design && (
            <button className="builder-btn-secondary" onClick={backToDesigns}>
              ← Changer de présentation
            </button>
          )}
          <span style={{ flex: 1 }} />
          {design && (
            <>
              <span className="canva-picker-hint">
                {selectedPage ? `Page ${selectedPage}` : pages.length ? "Aucune page choisie" : "Design entier"}
              </span>
              <button
                className="builder-btn-primary"
                disabled={!!busy || (pages.length > 0 && !selectedPage)}
                onClick={publish}
              >
                {slot ? "Mettre à jour l'image" : "Utiliser cette page"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
